import {NgModule, CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {
    HTTP_INTERCEPTORS,
    provideHttpClient, withFetch, withInterceptorsFromDi
} from '@angular/common/http';
import {
    IPublicClientApplication,
    PublicClientApplication,
    InteractionType
} from '@azure/msal-browser';
import {
    MsalGuard,
    MsalInterceptor,
    MsalBroadcastService,
    MsalInterceptorConfiguration,
    MsalService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalGuardConfiguration
} from '@azure/msal-angular';

import {environment} from '../environments/environment';

import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HomeComponent} from './home/home.component';
import {ErrorComponent} from './error/error.component';
import {SysadminSetInfomsgComponent} from './home/dialogs/sysadmin/sysadmin-set-infomsg/sysadmin-set-infomsg.component';
import {CafsSpinnerComponent} from './cafs-spinner/cafs-spinner.component';

import {SysadminDialog} from './home/dialogs/sysadmin/sysadmin-dialog';
import {ExpiredAssignmentsDialog} from './home/dialogs/startupmessages/expired-assignments-dialog';
import {CommentDialog} from './home/dialogs/comment-dialog';
import {ExtendDateDialog} from './home/dialogs/extend-date-dialog';
import {ConsultantGdprDialog} from './home/dialogs/consultant-gdpr-dialog';
import {UserGdprDialog} from './home/dialogs/user-gdpr-dialog';

import {EndDateDialog} from './home/dialogs/end-date-dialog';
import {ActiveConsOnInactiveAssignDialog} from './home/dialogs/startupmessages/active-cons-on-inactive-assign-dialog';
import {InternalMsgService} from './shared/internal.msg.service';
import {
    SysadminGiveDispensationComponent
} from './home/dialogs/sysadmin/sysadmin-give-dispensation/sysadmin-give-dispensation.component';
import {SysadminEditRoleComponent} from './home/dialogs/sysadmin/sysadmin-edit-role/sysadmin-edit-role.component';
import {DispensationDialog} from './home/dialogs/dispensation-dialog';
import {ToasterComponent} from './toaster/toaster.component';
import {ListPaginatorComponent} from './list-paginator/list-paginator.component';
import {BatchJobStatusDialog} from './home/dialogs/batch-job-status/batch-job-status-dialog';
import {EmailBatchJobStatus} from './home/dialogs/batch-job-status/email-batch-job-status/email-batch-job-status';
import {ImportBatchJobStatus} from './home/dialogs/batch-job-status/import-batch-job-status/import-batch-job-status';
import {NoCacheInterceptor} from "./no-cache.interceptor";

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(environment.msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(
        environment.webApi.uri,
        environment.webApi.scopes
    );
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.webApi.scopes],
        },
        loginFailedRoute: '/login-failed',
    };
}

@NgModule({

    declarations: [SysadminDialog,
        ExpiredAssignmentsDialog,
        EndDateDialog,
        ExtendDateDialog,
        ConsultantGdprDialog,
        UserGdprDialog,
        CommentDialog,
        ActiveConsOnInactiveAssignDialog,
        DispensationDialog,
        AppComponent,
        ErrorComponent,
        HomeComponent,
        ExtendDateDialog,
        CafsSpinnerComponent,
        ConsultantGdprDialog,
        UserGdprDialog,
        EndDateDialog,
        CommentDialog,
        BatchJobStatusDialog,
        EmailBatchJobStatus,
        ImportBatchJobStatus,
        ListPaginatorComponent,
        ActiveConsOnInactiveAssignDialog,
        SysadminSetInfomsgComponent,
        SysadminDialog,
        ExpiredAssignmentsDialog,
        SysadminGiveDispensationComponent,
        SysadminEditRoleComponent,
        DispensationDialog,
        ToasterComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        //SharedModule,
        CommonModule,
        FormsModule,
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NoCacheInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        InternalMsgService
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]

})
export class AppModule {
}
